import { useField, type FieldComponentProps } from "@w3rone/json-schema-form"
import { isValid } from "date-fns/isValid"
import { createContext, Fragment, ReactNode, useContext } from "react"
import { fromZonedTime } from "date-fns-tz"
import { DateTimePicker } from "app/components/DateTimePicker"
import { FormErrors } from "./FormErrors"

export const DateTimeField = ({ name }: FieldComponentProps) => {
  const {
    value: valueProps,
    schema,
    label,
    onChange,
    errors,
    id,
    description,
  } = useField(name)

  const value = valueProps ? new Date(String(valueProps)) : null

  const propProviderContext = useContext(DateFieldPropContext)
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

  return (
    <Fragment>
      <DateTimePicker
        disabled={schema.readOnly}
        label={label}
        value={value}
        onChange={(date) =>
          onChange?.(date && isValid(date) ? date.toISOString() : "")
        }
        minDate={propProviderContext?.minDate}
        maxDate={propProviderContext?.maxDate}
        slotProps={{
          textField: {
            id,
            helperText: description,
            error: errors.length > 0,
          },
        }}
      />
      {errors ? <FormErrors errors={errors} /> : null}
      <input
        type="hidden"
        name={name}
        value={value ? fromZonedTime(value, timeZone).toISOString() : ""}
        onChange={noop}
      />
    </Fragment>
  )
}

const DateFieldPropContext = createContext<
  { minDate?: Date; maxDate?: Date } | undefined
>(undefined)

export const DateFieldPropProvider = ({
  minDate,
  maxDate,
  children,
}: DateFieldPropProviderProps) => {
  return (
    <DateFieldPropContext.Provider value={{ minDate, maxDate }}>
      {children}
    </DateFieldPropContext.Provider>
  )
}

interface DateFieldPropProviderProps {
  minDate?: Date
  maxDate?: Date
  children: ReactNode
}

const noop = () => {
  // Intentionally does nothing
}
